import { useState, useEffect } from 'react';

const navItems = [
  { label: 'HOME', href: '#home' },
  { label: 'SERVICES', href: '#services' },
  { label: 'FLOW', href: '#flow' },
  { label: 'ABOUT US', href: '#about' },
  { label: 'CLIENTS', href: '#clients' },
  { label: 'FAQ', href: '#faq' },
  { label: 'CONTACT', href: '#contact' },
];

const sectionIds = ['home', 'services', 'flow', 'about', 'clients', 'faq', 'contact'];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeId, setActiveId] = useState('home');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { threshold: 0.35 }
    );
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const handleNav = (href: string) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    setMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/92 backdrop-blur-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-none w-full px-8 py-4 flex items-center justify-between">
        {/* Logo */}
        <button onClick={() => handleNav('#home')} className="flex items-center cursor-pointer">
          <img
            src="https://static.wixstatic.com/media/a314f7_845f62e0a1f24abca7a449a8f2d689fb~mv2.png/v1/fill/w_226,h_83,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/%E7%99%BD%E6%8A%9C%E3%81%8D.png"
            alt="Groove Gravity"
            className="h-9 w-auto object-contain"
          />
        </button>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navItems.map((item) => {
            const id = item.href.replace('#', '');
            const isActive = activeId === id;
            return (
              <button
                key={item.label}
                onClick={() => handleNav(item.href)}
                className={`relative text-xs font-medium tracking-widest transition-colors duration-200 cursor-pointer whitespace-nowrap pb-0.5 ${
                  isActive ? 'text-white' : 'text-white/50 hover:text-white/90'
                }`}
              >
                {item.label}
                {/* Active underline */}
                <span
                  className="absolute bottom-0 left-0 h-px bg-white transition-all duration-300"
                  style={{ width: isActive ? '100%' : '0%' }}
                />
              </button>
            );
          })}
        </nav>

        {/* Mobile hamburger */}
        <button
          className="lg:hidden text-white w-8 h-8 flex items-center justify-center cursor-pointer"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="メニュー"
        >
          <i className={`ri-${menuOpen ? 'close' : 'menu'}-line text-2xl`} />
        </button>
      </div>

      {/* Mobile Menu */}
      <div
        className="lg:hidden overflow-hidden bg-black/97 transition-all duration-300"
        style={{ maxHeight: menuOpen ? '480px' : '0px' }}
      >
        <div className="px-8 pb-6 pt-2">
          {navItems.map((item) => {
            const id = item.href.replace('#', '');
            const isActive = activeId === id;
            return (
              <button
                key={item.label}
                onClick={() => handleNav(item.href)}
                className={`flex items-center justify-between w-full text-left text-sm tracking-widest py-3.5 border-b border-white/8 cursor-pointer whitespace-nowrap transition-colors ${
                  isActive ? 'text-white' : 'text-white/50 hover:text-white/80'
                }`}
              >
                {item.label}
                {isActive && <i className="ri-arrow-right-line text-xs text-white/40" />}
              </button>
            );
          })}
        </div>
      </div>
    </header>
  );
}
